import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  Clock3,
  Edit3,
  FileText,
  Filter,
  Plus,
  Settings2,
  Trash2,
  Users,
  X,
} from "lucide-react";
import Select from "react-select";
import {
  createOvertimeEntry,
  createOvertimeOption,
  deleteOvertimeEntry,
  deleteOvertimeOption,
  getOvertimeContext,
  getOvertimeEntries,
  getOvertimePersonnel,
  getOvertimeSummary,
  updateOvertimeEntry,
} from "../../../api/overtimeApi";
import { useAuth } from "../../context/AuthContext";
import Layout from "../../layout/Layout";
import OvertimeBulkGrid from "./OvertimeBulkGrid";
import "./OvertimeTrackingPage.css";

const getLocalDate = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const emptyForm = () => ({
  employeeId: "",
  workDate: getLocalDate(),
  hours: "",
  reasonType: "STAFF_SHORTAGE",
  customReason: "",
  assignedWorkAreaId: "",
  reasonDetailId: "",
  note: "",
});

const emptySummary = {
  totals: {
    totalHours: 0,
    recordCount: 0,
    employeeCount: 0,
    departmentCount: 0,
  },
  departments: [],
  reasons: [],
  workAreas: [],
  assignmentBreakdown: [],
};

const errorMessage = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.msg ||
  error?.response?.data?.message ||
  "İşlem sırasında bir hata oluştu.";

const formatHours = (value) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(
    Number(value || 0)
  );

const formatDate = (value) =>
  new Date(value).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

function OvertimeTrackingPage() {
  const { user, permissions } = useAuth();
  const formRef = useRef(null);
  const [month, setMonth] = useState(getLocalDate().slice(0, 7));
  const [periodMode, setPeriodMode] = useState("month");
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [context, setContext] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });
  const [summary, setSummary] = useState(emptySummary);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showOptionManager, setShowOptionManager] = useState(false);
  const [optionSaving, setOptionSaving] = useState(false);
  const [optionForm, setOptionForm] = useState({
    optionType: "WORK_AREA",
    reasonType: "STAFF_SHORTAGE",
    label: "",
  });

  const canCreate = permissions.includes("overtime_create");
  const canManage = permissions.includes("overtime_manage");
  const [year, monthNumber] = month.split("-").map(Number);
  const reportYear = periodMode === "year" ? annualYear : year;
  const reportMonth = periodMode === "month" ? monthNumber : undefined;
  const periodLabel = periodMode === "year" ? `${annualYear} yılı` : `${month} dönemi`;

  const loadReferenceData = useCallback(async () => {
    try {
      const requests = [getOvertimeContext()];
      if (canCreate) requests.push(getOvertimePersonnel());
      const [contextResponse, personnelResponse] = await Promise.all(requests);

      setContext(contextResponse.data.data);
      if (personnelResponse) {
        setPersonnel(personnelResponse.data.data.personnel || []);
      }
    } catch (error) {
      setFeedback({ type: "error", message: errorMessage(error) });
    }
  }, [canCreate]);

  const loadReportData = useCallback(async () => {
    if (!reportYear || (periodMode === "month" && !reportMonth)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const period = {
        year: reportYear,
        ...(reportMonth ? { month: reportMonth } : {}),
      };
      const [entryResponse, summaryResponse] = await Promise.all([
        getOvertimeEntries({
          ...period,
          department: departmentFilter || undefined,
          search: search || undefined,
          page,
          limit: 100,
        }),
        getOvertimeSummary(period),
      ]);

      setEntries(entryResponse.data.data.entries || []);
      setPagination(entryResponse.data.data.pagination);
      setSummary(summaryResponse.data.data || emptySummary);
    } catch (error) {
      setFeedback({ type: "error", message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [departmentFilter, page, periodMode, reportMonth, reportYear, search]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const selectedPerson = personnel.find(
    (employee) => employee._id === form.employeeId
  );

  const personnelOptions = useMemo(
    () =>
      personnel.map((employee) => ({
        value: employee._id,
        label: `${employee.fullName} ${employee.department} ${
          employee.position || ""
        }`,
        employee,
      })),
    [personnel]
  );

  const selectedPersonnelOption =
    personnelOptions.find((option) => option.value === form.employeeId) || null;

  const availableReasonDetails = context?.reasonDetails?.[form.reasonType] || [];

  const recordDepartments = useMemo(
    () =>
      summary.departments
        .map((item) => item.department)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "tr")),
    [summary.departments]
  );

  const maxDepartmentHours = Math.max(
    1,
    ...summary.departments.map((item) => Number(item.totalHours || 0))
  );

  const maxWorkAreaCount = Math.max(
    1,
    ...summary.workAreas.map((item) => Number(item.recordCount || 0))
  );

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const nextForm = { ...current, [name]: value };

      if (name === "reasonType") {
        if (value !== "OTHER") nextForm.customReason = "";
        nextForm.reasonDetailId = "";
      }

      return nextForm;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    if (!context?.departmentConfigured) {
      setFeedback({
        type: "error",
        message: "Kullanıcı hesabınızda departman tanımlı değil.",
      });
      return;
    }

    if (!form.employeeId) {
      setFeedback({ type: "error", message: "Lütfen bir personel seçin." });
      return;
    }

    try {
      setSaving(true);
      const payload = { ...form, hours: Number(form.hours) };

      if (editingId) {
        await updateOvertimeEntry({ id: editingId, ...payload });
      } else {
        await createOvertimeEntry(payload);
      }

      setFeedback({
        type: "success",
        message: editingId
          ? "Mesai kaydı güncellendi."
          : "Mesai kaydı başarıyla oluşturuldu.",
      });
      resetForm();
      await Promise.all([loadReportData(), loadReferenceData()]);
    } catch (error) {
      setFeedback({ type: "error", message: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingId(entry._id);
    setForm({
      employeeId: String(entry.employeeId),
      workDate: entry.workDate.slice(0, 10),
      hours: String(entry.hours),
      reasonType: entry.reasonType,
      customReason: entry.customReason || "",
      assignedWorkAreaId: String(entry.assignedWorkAreaId || ""),
      reasonDetailId: String(entry.reasonDetailId || ""),
      note: entry.note || "",
    });
    setFeedback(null);
  };

  useEffect(() => {
    if (editingId) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editingId]);

  const handleDelete = async (entry) => {
    const confirmed = window.confirm(
      `${entry.employee.fullName} için ${formatDate(
        entry.workDate
      )} tarihli mesai kaydı silinsin mi?`
    );
    if (!confirmed) return;

    try {
      await deleteOvertimeEntry(entry._id);
      setFeedback({ type: "success", message: "Mesai kaydı silindi." });
      if (editingId === entry._id) resetForm();
      if (entries.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        await loadReportData();
      }
    } catch (error) {
      setFeedback({ type: "error", message: errorMessage(error) });
    }
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleCreateOption = async (event) => {
    event.preventDefault();
    if (!optionForm.label.trim()) return;

    try {
      setOptionSaving(true);
      await createOvertimeOption({
        optionType: optionForm.optionType,
        label: optionForm.label.trim(),
        ...(optionForm.optionType === "REASON_DETAIL"
          ? { reasonType: optionForm.reasonType }
          : {}),
      });
      setOptionForm((current) => ({ ...current, label: "" }));
      setFeedback({ type: "success", message: "Yeni mesai parametresi eklendi." });
      await loadReferenceData();
    } catch (error) {
      setFeedback({ type: "error", message: errorMessage(error) });
    } finally {
      setOptionSaving(false);
    }
  };

  const handleDeleteOption = async (option) => {
    if (!window.confirm(`“${option.label}” seçeneği pasife alınsın mı?`)) return;

    try {
      await deleteOvertimeOption(option._id);
      setForm((current) => ({
        ...current,
        assignedWorkAreaId:
          current.assignedWorkAreaId === option._id ? "" : current.assignedWorkAreaId,
        reasonDetailId:
          current.reasonDetailId === option._id ? "" : current.reasonDetailId,
      }));
      setFeedback({ type: "success", message: "Mesai parametresi pasife alındı." });
      await loadReferenceData();
    } catch (error) {
      setFeedback({ type: "error", message: errorMessage(error) });
    }
  };

  return (
    <Layout>
      <div className="overtime-page">
        <section className="overtime-hero">
          <div className="overtime-hero-copy">
            <div className="overtime-kicker">
              <Clock3 size={16} /> Ortak modüller
            </div>
            <h1>Mesai Takibi</h1>
            <p>
              Personel bazlı fazla mesai kayıtlarını oluşturun. Aylık toplamları,
              mesai yazdıran departmanları ve nedenleri tek ekrandan izleyin.
            </p>
          </div>
          <div className="overtime-department-card">
            <span>Mesaiyi yazdıran departman</span>
            <strong>
              {context?.requester?.departmentName ||
                user?.departmanName ||
                "Tanımlanmamış"}
            </strong>
            {context?.requester?.subDepartmentName && (
              <small>{context.requester.subDepartmentName}</small>
            )}
          </div>
        </section>

        {feedback && (
          <div className={`overtime-feedback ${feedback.type}`} role="alert">
            {feedback.message}
          </div>
        )}

        {canManage && (
          <div className="overtime-option-toolbar">
            <button
              className="overtime-secondary-btn"
              type="button"
              onClick={() => setShowOptionManager((current) => !current)}
            >
              <Settings2 size={16} />
              {showOptionManager ? "Parametre Yönetimini Kapat" : "Bölüm ve Neden Parametreleri"}
            </button>
          </div>
        )}

        {canManage && showOptionManager && (
          <section className="overtime-panel overtime-option-manager">
            <div className="overtime-panel-header">
              <div>
                <h2>Mesai parametreleri</h2>
                <p>Bölüm/hat ve ana nedene bağlı alt neden seçeneklerini yönetin.</p>
              </div>
            </div>

            <form className="overtime-option-form" onSubmit={handleCreateOption}>
              <select
                value={optionForm.optionType}
                onChange={(event) =>
                  setOptionForm((current) => ({
                    ...current,
                    optionType: event.target.value,
                  }))
                }
              >
                <option value="WORK_AREA">Görevlendirme bölümü / hat</option>
                <option value="REASON_DETAIL">Neden detayı</option>
              </select>

              {optionForm.optionType === "REASON_DETAIL" && (
                <select
                  value={optionForm.reasonType}
                  onChange={(event) =>
                    setOptionForm((current) => ({
                      ...current,
                      reasonType: event.target.value,
                    }))
                  }
                >
                  {(context?.reasons || []).map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              )}

              <input
                value={optionForm.label}
                onChange={(event) =>
                  setOptionForm((current) => ({
                    ...current,
                    label: event.target.value,
                  }))
                }
                maxLength={180}
                placeholder="Yeni seçenek adı"
                required
              />
              <button className="overtime-primary-btn" type="submit" disabled={optionSaving}>
                <Plus size={16} /> {optionSaving ? "Ekleniyor..." : "Seçenek Ekle"}
              </button>
            </form>

            <div className="overtime-option-list">
              {(optionForm.optionType === "WORK_AREA"
                ? context?.workAreas || []
                : context?.reasonDetails?.[optionForm.reasonType] || []
              ).map((option) => (
                <span className="overtime-option-chip" key={option._id}>
                  {option.label}
                  <button
                    type="button"
                    onClick={() => handleDeleteOption(option)}
                    title="Pasife al"
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          </section>
        )}

        <section className="overtime-summary-grid" aria-label="Dönem özeti">
          <SummaryCard
            icon={<Clock3 size={21} />}
            label="Toplam mesai"
            value={`${formatHours(summary.totals.totalHours)} saat`}
          />
          <SummaryCard
            icon={<FileText size={21} />}
            label="Kayıt sayısı"
            value={summary.totals.recordCount}
          />
          <SummaryCard
            icon={<Users size={21} />}
            label="Mesai yapan personel"
            value={summary.totals.employeeCount}
          />
          <SummaryCard
            icon={<Building2 size={21} />}
            label="Mesai yazdıran departman"
            value={summary.totals.departmentCount}
          />
        </section>

        {canCreate && !editingId && (
          <section className="overtime-panel overtime-bulk-panel">
            <div className="overtime-panel-header">
              <div>
                <h2>Yeni mesai kaydı</h2>
                <p>
                  Personeller solda, mesai nedenleri başlıkta. Tıklayarak seçin, tek
                  seferde kaydedin.
                </p>
              </div>
            </div>
            {!context?.departmentConfigured && context && (
              <div className="overtime-warning">
                Mesai kaydı oluşturmak için hesabınıza departman tanımlanmalıdır.
              </div>
            )}
            {personnel.length === 0 && (
              <div className="overtime-warning">
                Personel listesi henüz sisteme aktarılmamış.
              </div>
            )}
            <OvertimeBulkGrid
              personnel={personnel}
              context={context}
              disabled={!context?.departmentConfigured || personnel.length === 0}
              onFeedback={setFeedback}
              onSaved={() => Promise.all([loadReportData(), loadReferenceData()])}
            />
          </section>
        )}

        <div
          className={`overtime-main-grid ${
            !canCreate || !editingId ? "viewer-only" : ""
          }`}
        >
          {canCreate && editingId && (
            <section className="overtime-panel" ref={formRef}>
              <div className="overtime-panel-header">
                <div>
                  <h2>
                    {editingId
                      ? "Mesai kaydını düzenle"
                      : "Yeni mesai kaydı"}
                  </h2>
                  <p>Personel ve mesai bilgisini girin.</p>
                </div>
                {editingId && (
                  <button
                    className="overtime-icon-btn"
                    type="button"
                    onClick={resetForm}
                    title="Düzenlemeyi kapat"
                  >
                    <X size={17} />
                  </button>
                )}
              </div>

              {!context?.departmentConfigured && context && (
                <div className="overtime-warning">
                  Mesai kaydı oluşturmak için hesabınıza departman
                  tanımlanmalıdır.
                </div>
              )}
              {personnel.length === 0 && (
                <div className="overtime-warning">
                  Personel listesi henüz sisteme aktarılmamış.
                </div>
              )}

              <form className="overtime-form" onSubmit={handleSubmit}>
                <div className="overtime-field">
                  <label htmlFor="employeeId">Personel</label>
                  <Select
                    inputId="employeeId"
                    instanceId="overtime-employee-select"
                    className="overtime-personnel-select"
                    classNamePrefix="overtime-personnel-select"
                    options={personnelOptions}
                    value={selectedPersonnelOption}
                    onChange={(option) =>
                      setForm((current) => ({
                        ...current,
                        employeeId: option?.value || "",
                      }))
                    }
                    formatOptionLabel={({ employee }) => (
                      <div className="overtime-personnel-option">
                        <strong>{employee.fullName}</strong>
                        <small>
                          {employee.department}
                          {employee.position ? ` · ${employee.position}` : ""}
                        </small>
                      </div>
                    )}
                    isClearable
                    isSearchable
                    isDisabled={personnel.length === 0}
                    placeholder="İsim yazarak personel seçin..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue
                        ? "Aramanızla eşleşen personel bulunamadı"
                        : "Personel bulunamadı"
                    }
                  />
                  {selectedPerson && (
                    <div className="overtime-selected-person">
                      <span>Mevcut departman</span>
                      <strong>{selectedPerson.department}</strong>
                      <small>
                        {selectedPerson.position || "Görev belirtilmemiş"}
                      </small>
                    </div>
                  )}
                </div>

                <div className="overtime-field-row">
                  <div className="overtime-field">
                    <label htmlFor="workDate">Mesai tarihi</label>
                    <input
                      id="workDate"
                      name="workDate"
                      type="date"
                      value={form.workDate}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="overtime-field">
                    <label htmlFor="hours">Mesai süresi (saat)</label>
                    <input
                      id="hours"
                      name="hours"
                      type="number"
                      min="0.25"
                      max="24"
                      step="0.25"
                      value={form.hours}
                      onChange={handleFormChange}
                      placeholder="Örn. 2,5"
                      required
                    />
                  </div>
                </div>

                <div className="overtime-field">
                  <label htmlFor="reasonType">Mesai nedeni</label>
                  <select
                    id="reasonType"
                    name="reasonType"
                    value={form.reasonType}
                    onChange={handleFormChange}
                    required
                  >
                    {(context?.reasons || []).map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="overtime-field-row">
                  <div className="overtime-field">
                    <label htmlFor="assignedWorkAreaId">
                      Mesaiye gönderilen bölüm / hat
                    </label>
                    <select
                      id="assignedWorkAreaId"
                      name="assignedWorkAreaId"
                      value={form.assignedWorkAreaId}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Bölüm / hat seçin</option>
                      {(context?.workAreas || []).map((workArea) => (
                        <option key={workArea._id} value={workArea._id}>
                          {workArea.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="overtime-field">
                    <label htmlFor="reasonDetailId">Neden detayı</label>
                    <select
                      id="reasonDetailId"
                      name="reasonDetailId"
                      value={form.reasonDetailId}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Alt neden seçin</option>
                      {availableReasonDetails.map((detail) => (
                        <option key={detail._id} value={detail._id}>
                          {detail.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {form.reasonType === "OTHER" && (
                  <div className="overtime-field">
                    <label htmlFor="customReason">Diğer neden</label>
                    <input
                      id="customReason"
                      name="customReason"
                      value={form.customReason}
                      onChange={handleFormChange}
                      maxLength={180}
                      placeholder="Mesai nedenini yazın"
                      required
                    />
                  </div>
                )}

                <div className="overtime-field">
                  <label htmlFor="note">Not (isteğe bağlı)</label>
                  <textarea
                    id="note"
                    name="note"
                    value={form.note}
                    onChange={handleFormChange}
                    maxLength={500}
                    placeholder="Ek açıklama"
                  />
                </div>

                <div className="overtime-actions">
                  <button
                    className="overtime-primary-btn"
                    type="submit"
                    disabled={
                      saving ||
                      !context?.departmentConfigured ||
                      personnel.length === 0
                    }
                  >
                    <Plus size={17} />
                    {saving
                      ? "Kaydediliyor..."
                      : editingId
                        ? "Değişiklikleri kaydet"
                        : "Mesai kaydı oluştur"}
                  </button>
                  {editingId && (
                    <button
                      className="overtime-secondary-btn"
                      type="button"
                      onClick={resetForm}
                      disabled={saving}
                    >
                      Vazgeç
                    </button>
                  )}
                </div>
              </form>
            </section>
          )}

          <div
            className={`overtime-content-stack ${
              canCreate && !editingId ? "two-col" : ""
            }`}
          >
            <section className="overtime-panel">
              <div className="overtime-panel-header">
                <div>
                  <h3>Mesai yazdıran departmanlar</h3>
                  <p>{periodLabel} içinde talep açan departmanların toplamları</p>
                </div>
              </div>
              {summary.departments.length ? (
                <div className="overtime-chart-list">
                  {summary.departments.map((item) => (
                    <div key={item.department}>
                      <div className="overtime-chart-row-header">
                        <span>
                          {item.department} · {item.employeeCount} personel
                        </span>
                        <strong>{formatHours(item.totalHours)} saat</strong>
                      </div>
                      <div className="overtime-chart-track">
                        <div
                          className="overtime-chart-fill"
                          style={{
                            width: `${Math.max(
                              3,
                              (Number(item.totalHours) / maxDepartmentHours) *
                                100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overtime-empty">
                  Bu dönem için departman verisi yok.
                </div>
              )}
            </section>

            <section className="overtime-panel">
              <div className="overtime-panel-header">
                <div>
                  <h3>Mesai nedenleri</h3>
                  <p>{periodLabel} için ana neden bazında saat toplamı</p>
                </div>
              </div>
              {summary.reasons.length ? (
                <div className="overtime-chart-list">
                  {summary.reasons.map((item) => (
                    <div
                      className="overtime-chart-row-header"
                      key={item.reasonType}
                    >
                      <span>{item.reasonLabel}</span>
                      <strong>{formatHours(item.totalHours)} saat</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overtime-empty">Bu dönem için neden verisi yok.</div>
              )}
            </section>

            <section className="overtime-panel">
              <div className="overtime-panel-header">
                <div>
                  <h3>Mesaiye personel gönderilen bölümler</h3>
                  <p>Kaç görevlendirme yapıldığı ve toplam mesai süresi</p>
                </div>
              </div>
              {summary.workAreas.length ? (
                <div className="overtime-chart-list">
                  {summary.workAreas.map((item) => (
                    <div key={item.workArea}>
                      <div className="overtime-chart-row-header">
                        <span>
                          {item.workArea} · {item.recordCount} kez · {item.employeeCount} personel
                        </span>
                        <strong>{formatHours(item.totalHours)} saat</strong>
                      </div>
                      <div className="overtime-chart-track">
                        <div
                          className="overtime-chart-fill overtime-chart-fill--count"
                          style={{
                            width: `${Math.max(
                              3,
                              (Number(item.recordCount) / maxWorkAreaCount) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overtime-empty">Bu dönem için bölüm verisi yok.</div>
              )}
            </section>

            <section className="overtime-panel">
              <div className="overtime-panel-header">
                <div>
                  <h3>Bölüm ve neden kırılımı</h3>
                  <p>Toplantılar için görevlendirme bölümünün detaylı gerekçesi</p>
                </div>
              </div>
              {summary.assignmentBreakdown.length ? (
                <div className="overtime-breakdown-list">
                  {summary.assignmentBreakdown.map((item) => (
                    <div
                      className="overtime-breakdown-row"
                      key={`${item.workArea}-${item.reasonType}-${item.reasonDetail}`}
                    >
                      <div>
                        <strong>{item.workArea}</strong>
                        <span>{item.reasonLabel} → {item.reasonDetail}</span>
                      </div>
                      <div>
                        <strong>{item.recordCount} kez</strong>
                        <span>{formatHours(item.totalHours)} saat</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overtime-empty">Bu dönem için detay kırılımı yok.</div>
              )}
            </section>
          </div>
        </div>

        <section className="overtime-panel" style={{ marginTop: 20 }}>
          <div className="overtime-panel-header">
            <div>
              <h3>Mesai kayıtları</h3>
              <p>Seçilen dönemdeki personel mesai hareketleri</p>
            </div>
          </div>

          <form className="overtime-filter-bar" onSubmit={applyFilters}>
            <select
              aria-label="Rapor görünümü"
              value={periodMode}
              onChange={(event) => {
                setPeriodMode(event.target.value);
                setPage(1);
              }}
            >
              <option value="month">Aylık görünüm</option>
              <option value="year">Yıllık görünüm</option>
            </select>

            {periodMode === "month" ? (
              <input
                type="month"
                aria-label="Rapor dönemi"
                value={month}
                onChange={(event) => {
                  setMonth(event.target.value);
                  setPage(1);
                }}
                required
              />
            ) : (
              <input
                type="number"
                aria-label="Rapor yılı"
                min="2020"
                max="2100"
                value={annualYear}
                onChange={(event) => {
                  setAnnualYear(Number(event.target.value));
                  setPage(1);
                }}
                required
              />
            )}
            <select
              aria-label="Mesaiyi yazdıran departman"
              value={departmentFilter}
              onChange={(event) => {
                setDepartmentFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Tüm mesai yazdıran departmanlar</option>
              {recordDepartments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
            <input
              aria-label="Mesai kaydı ara"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Personel, bölüm, neden veya not ara"
            />
            <button className="overtime-filter-button" type="submit">
              <Filter size={16} /> Filtrele
            </button>
          </form>

          {loading ? (
            <div className="overtime-loading">
              <span className="overtime-spinner" />
              <div>Kayıtlar yükleniyor...</div>
            </div>
          ) : entries.length ? (
            <div className="overtime-table-wrap">
              <table className="overtime-table">
                <thead>
                  <tr>
                    <th>Personel</th>
                    <th>Personelin departmanı</th>
                    <th>Tarih</th>
                    <th>Süre</th>
                    <th>Neden</th>
                    <th>Görevlendirilen bölüm / Alt neden</th>
                    <th>Mesaiyi yazdıran</th>
                    {(canCreate || canManage) && <th>İşlem</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry._id}>
                      <td className="overtime-person-cell">
                        <strong>{entry.employee.fullName}</strong>
                        <small>{entry.employee.position || "Görev yok"}</small>
                      </td>
                      <td>
                        <span className="overtime-department-badge">
                          {entry.employee.department}
                        </span>
                      </td>
                      <td>{formatDate(entry.workDate)}</td>
                      <td>
                        <span className="overtime-hour-badge">
                          {formatHours(entry.hours)} saat
                        </span>
                      </td>
                      <td className="overtime-person-cell">
                        <strong>{entry.reasonLabel}</strong>
                        {entry.note && <small>{entry.note}</small>}
                      </td>
                      <td className="overtime-person-cell">
                        <strong>
                          {entry.assignedWorkAreaLabel || "Belirtilmemiş"}
                        </strong>
                        <small>
                          {entry.reasonDetailLabel || "Alt neden belirtilmemiş"}
                        </small>
                      </td>
                      <td className="overtime-person-cell">
                        <strong>{entry.requester.departmentName}</strong>
                        <small>Kaydı açan: {entry.requester.fullName}</small>
                      </td>
                      {(canCreate || canManage) && (
                        <td>
                          <div className="overtime-row-actions">
                            {entry.canEdit && canCreate && (
                              <button
                                className="overtime-icon-btn"
                                type="button"
                                onClick={() => handleEdit(entry)}
                                title="Düzenle"
                              >
                                <Edit3 size={16} />
                              </button>
                            )}
                            {entry.canDelete && canManage && (
                              <button
                                className="overtime-icon-btn danger"
                                type="button"
                                onClick={() => handleDelete(entry)}
                                title="Sil"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {pagination.totalPages > 1 && (
                <div className="overtime-pagination">
                  <span>
                    Toplam {pagination.total} kayıt · Sayfa {pagination.page} /{" "}
                    {pagination.totalPages}
                  </span>
                  <div>
                    <button
                      type="button"
                      onClick={() => setPage((current) => current - 1)}
                      disabled={page <= 1}
                    >
                      Önceki
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((current) => current + 1)}
                      disabled={page >= pagination.totalPages}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="overtime-empty">
              Seçilen filtrelerde mesai kaydı bulunamadı.
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function SummaryCard({ icon, label, value }) {
  return (
    <div className="overtime-stat-card">
      <div className="overtime-stat-icon">{icon}</div>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

export default OvertimeTrackingPage;
